import { useState } from 'react';
import { fetchGetStateByStateKey } from '@iwoca/lapi-client/edge';
import { Button } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import { useAmplitude } from 'hooks/useAmplitude';
import { TGetStateResponseData } from 'lendingApi/lapiOverrides.types';
import { useGetIntroducerApplication } from 'lendingApi/lendingApiHooks';
import { useNavigate } from 'react-router-dom';
import { TSignupSteps } from 'types';
import { checkApplicationIsComplete } from 'utils/application/application.helpers';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

export const ContinueApplicationButton = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const [isLoading, setIsLoading] = useState(false);

  const { application } = useGetIntroducerApplication();
  const applicationId = application?.application_uid;
  const stateKey = application?.customer?.state_key;

  const getRedirectUrl = (latestPage?: TSignupSteps) => {
    if (!latestPage) {
      return PATH_NAMES.newApplicationStep1({ applicationId });
    }

    return PATH_NAMES.newApplication({ step: latestPage, applicationId });
  };

  const handleRedirect = async () => {
    trackEvent(AMPLITUDE_EVENTS.dashboardCTAClicked, {
      Status: 'Open',
    });

    trackEvent(AMPLITUDE_EVENTS.signupCTAClicked, {
      'CTA location': 'Application table',
      'CTA text': 'Continue app',
      'CTA URL': getRedirectUrl(),
    });

    try {
      setIsLoading(true);
      if (!stateKey) {
        return;
      }
      const response = await fetchGetStateByStateKey({
        stateKey,
      });
      const state = response.data as TGetStateResponseData;
      navigate(getRedirectUrl(state.ui?.tip_latest_signup_step));
    } catch {
      // If error, redirect to signup step 1
      navigate(getRedirectUrl());
    } finally {
      setIsLoading(false);
    }
  };

  if (checkApplicationIsComplete(application)) {
    return null;
  }

  return (
    <Button
      loading={isLoading}
      size="small"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleRedirect();
      }}
    >
      <Trans>Continue app</Trans>
    </Button>
  );
};
